import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import axios from "axios";
import { Loader2, Save, Lock, User, Mail, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useClientAuth } from "@/lib/clientAuth";
import PortalShell from "@/components/PortalShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClientAccount() {
    const { client, token, loading, logout } = useClientAuth();
    const [profile, setProfile] = useState(null);
    const [busy, setBusy] = useState(true);
    const [savingProfile, setSavingProfile] = useState(false);
    const [savingPassword, setSavingPassword] = useState(false);

    const [form, setForm] = useState({ name: "", company: "" });
    const [pwd, setPwd] = useState({
        current_password: "",
        new_password: "",
        confirm: "",
    });

    const headers = useMemo(
        () => ({ Authorization: `Bearer ${token}` }),
        [token],
    );

    useEffect(() => {
        if (!token) return;
        axios
            .get(`${API}/clients/me`, { headers })
            .then((r) => {
                setProfile(r.data);
                setForm({
                    name: r.data.name || "",
                    company: r.data.company || "",
                });
            })
            .catch((err) => {
                if (err?.response?.status === 401) logout();
            })
            .finally(() => setBusy(false));
    }, [token, headers, logout]);

    if (loading)
        return (
            <div className="min-h-screen bg-ink flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-ember" />
            </div>
        );
    if (!client) return <Navigate to="/portal/login" replace />;

    const onSaveProfile = async (e) => {
        e.preventDefault();
        if (!form.name.trim()) {
            toast.error("Name required");
            return;
        }
        setSavingProfile(true);
        try {
            const { data } = await axios.patch(
                `${API}/clients/me`,
                { name: form.name, company: form.company },
                { headers },
            );
            setProfile(data);
            toast.success("Profile updated");
        } catch {
            toast.error("Could not save profile");
        } finally {
            setSavingProfile(false);
        }
    };

    const onChangePassword = async (e) => {
        e.preventDefault();
        if (pwd.new_password.length < 8) {
            toast.error("New password must be at least 8 characters");
            return;
        }
        if (pwd.new_password !== pwd.confirm) {
            toast.error("Passwords don't match");
            return;
        }
        setSavingPassword(true);
        try {
            await axios.post(
                `${API}/clients/change-password`,
                {
                    current_password: pwd.current_password,
                    new_password: pwd.new_password,
                },
                { headers },
            );
            toast.success("Password changed");
            setPwd({ current_password: "", new_password: "", confirm: "" });
        } catch (err) {
            const msg =
                err?.response?.data?.detail || "Could not change password";
            toast.error(typeof msg === "string" ? msg : "Failed");
        } finally {
            setSavingPassword(false);
        }
    };

    return (
        <PortalShell>
            <div className="mb-10">
                <div className="text-[10px] uppercase tracking-[0.25em] text-ember mb-3">
                    Account
                </div>
                <h1
                    data-testid="account-heading"
                    className="font-display text-bone text-5xl tracking-tightest leading-[1]"
                >
                    Your details.
                </h1>
            </div>

            {busy ? (
                <div className="flex items-center gap-2 text-bone-500">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading…
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                    {/* Profile */}
                    <form
                        onSubmit={onSaveProfile}
                        data-testid="profile-form"
                        className="lg:col-span-7 border border-white/10 bg-ink-800/40 p-8"
                    >
                        <h2 className="font-display text-bone text-xl tracking-tight mb-6 inline-flex items-center gap-2">
                            <User className="w-5 h-5 text-ember" />
                            Profile
                        </h2>
                        <Field
                            label="Name"
                            value={form.name}
                            onChange={(v) =>
                                setForm((f) => ({ ...f, name: v }))
                            }
                            testid="profile-name"
                        />
                        <Field
                            label="Company"
                            value={form.company}
                            onChange={(v) =>
                                setForm((f) => ({ ...f, company: v }))
                            }
                            testid="profile-company"
                        />
                        <ReadOnly
                            label="Email"
                            value={profile?.email}
                        />
                        <button
                            type="submit"
                            disabled={savingProfile}
                            data-testid="save-profile"
                            className="btn-ember inline-flex items-center gap-2 h-11 px-5 text-xs uppercase tracking-[0.2em] disabled:opacity-60"
                        >
                            {savingProfile ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <Save className="w-4 h-4" />
                            )}
                            Save profile
                        </button>
                    </form>

                    {/* Plan + Manager + Password */}
                    <div className="lg:col-span-5 space-y-6">
                        <div className="border border-white/10 bg-ink-800/40 p-6">
                            <h2 className="text-[10px] uppercase tracking-[0.25em] text-bone-500 mb-3">
                                Your plan
                            </h2>
                            {profile?.plan ? (
                                <>
                                    <div className="font-display text-bone text-xl tracking-tight">
                                        {profile.plan}
                                    </div>
                                    {profile.plan_summary && (
                                        <p className="text-bone-300 text-sm leading-relaxed mt-2 whitespace-pre-wrap">
                                            {profile.plan_summary}
                                        </p>
                                    )}
                                </>
                            ) : (
                                <p className="text-bone-500 text-sm">
                                    Plan details will appear here once
                                    confirmed.
                                </p>
                            )}
                        </div>

                        <div className="border border-white/10 bg-ink-800/40 p-6">
                            <h2 className="text-[10px] uppercase tracking-[0.25em] text-bone-500 mb-3">
                                Account manager
                            </h2>
                            {profile?.manager_name || profile?.manager_email ? (
                                <>
                                    <div className="font-display text-bone text-lg tracking-tight">
                                        {profile.manager_name || "—"}
                                    </div>
                                    {profile.manager_role && (
                                        <div className="text-bone-500 text-xs uppercase tracking-[0.2em] mb-2">
                                            {profile.manager_role}
                                        </div>
                                    )}
                                    {profile.manager_email && (
                                        <a
                                            href={`mailto:${profile.manager_email}`}
                                            className="inline-flex items-center gap-2 mt-3 text-ember hover:underline text-sm"
                                            data-testid="account-manager-email"
                                        >
                                            <Mail className="w-3.5 h-3.5" />
                                            {profile.manager_email}
                                        </a>
                                    )}
                                </>
                            ) : (
                                <p className="text-bone-500 text-sm">
                                    Not yet assigned.
                                </p>
                            )}
                        </div>

                        <form
                            onSubmit={onChangePassword}
                            data-testid="password-form"
                            className="border border-white/10 bg-ink-800/40 p-6"
                        >
                            <h2 className="font-display text-bone text-xl tracking-tight mb-5 inline-flex items-center gap-2">
                                <Lock className="w-5 h-5 text-ember" />
                                Change password
                            </h2>
                            <Field
                                label="Current password"
                                type="password"
                                value={pwd.current_password}
                                onChange={(v) =>
                                    setPwd((s) => ({
                                        ...s,
                                        current_password: v,
                                    }))
                                }
                                testid="pwd-current"
                            />
                            <Field
                                label="New password"
                                type="password"
                                value={pwd.new_password}
                                onChange={(v) =>
                                    setPwd((s) => ({
                                        ...s,
                                        new_password: v,
                                    }))
                                }
                                testid="pwd-new"
                            />
                            <Field
                                label="Confirm new password"
                                type="password"
                                value={pwd.confirm}
                                onChange={(v) =>
                                    setPwd((s) => ({ ...s, confirm: v }))
                                }
                                testid="pwd-confirm"
                            />
                            <button
                                type="submit"
                                disabled={savingPassword}
                                data-testid="save-password"
                                className="btn-ember inline-flex items-center gap-2 h-11 px-5 text-xs uppercase tracking-[0.2em] disabled:opacity-60"
                            >
                                {savingPassword ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <Save className="w-4 h-4" />
                                )}
                                Update password
                            </button>
                        </form>
                    </div>
                </div>
            )}

            {/* Danger zone — self-serve account deletion (GDPR) */}
            {profile && (
                <div
                    data-testid="danger-zone"
                    className="mt-12 border border-rose-500/25 bg-rose-500/[0.03] p-6 lg:p-8"
                >
                    <div className="flex items-start gap-4 mb-4">
                        <AlertTriangle className="w-5 h-5 text-rose-400 shrink-0 mt-0.5" />
                        <div>
                            <h3 className="font-display text-bone text-xl tracking-tight">
                                Delete my account
                            </h3>
                            <p className="text-sm text-bone-500 mt-1 leading-relaxed">
                                Removes your personal information (name, email,
                                phone, company) and revokes access. Historical
                                invoices remain anonymized in our books for
                                accounting compliance. This cannot be undone.
                            </p>
                        </div>
                    </div>
                    <DeleteAccountButton token={token} onDone={logout} />
                </div>
            )}
        </PortalShell>
    );
}

function DeleteAccountButton({ token, onDone }) {
    const [busy, setBusy] = useState(false);
    const [confirmText, setConfirmText] = useState("");
    const [step, setStep] = useState("idle"); // idle | confirming

    const del = async () => {
        setBusy(true);
        try {
            await axios.post(
                `${API}/clients/delete-account`,
                {},
                { headers: { Authorization: `Bearer ${token}` } },
            );
            toast.success("Your account has been deleted.");
            setTimeout(() => onDone(), 800);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Deletion failed. Please contact support.");
        } finally {
            setBusy(false);
        }
    };

    if (step === "idle") {
        return (
            <button
                type="button"
                onClick={() => setStep("confirming")}
                data-testid="delete-account-open"
                className="inline-flex items-center gap-2 h-11 px-5 border border-rose-500/30 text-rose-300 hover:border-rose-500/60 hover:text-rose-200 transition-colors text-xs uppercase tracking-[0.2em]"
            >
                <Trash2 className="w-4 h-4" />
                Delete my account
            </button>
        );
    }

    return (
        <div className="border border-rose-500/40 bg-rose-500/[0.05] p-4 space-y-3">
            <p className="text-sm text-bone">
                Type <span className="font-mono text-rose-300">DELETE</span> to confirm.
            </p>
            <input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                data-testid="delete-account-confirm-input"
                className="w-full bg-transparent border border-white/10 focus:border-rose-500 outline-none px-3 py-2 text-bone"
                autoFocus
            />
            <div className="flex items-center gap-3">
                <button
                    type="button"
                    disabled={busy || confirmText.trim() !== "DELETE"}
                    onClick={del}
                    data-testid="delete-account-submit"
                    className="inline-flex items-center gap-2 h-10 px-4 bg-rose-500/90 hover:bg-rose-500 text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-xs uppercase tracking-[0.2em]"
                >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                    Permanently delete
                </button>
                <button
                    type="button"
                    onClick={() => {
                        setStep("idle");
                        setConfirmText("");
                    }}
                    className="text-xs uppercase tracking-[0.2em] text-bone-500 hover:text-bone transition-colors"
                >
                    Cancel
                </button>
            </div>
        </div>
    );
}

function Field({ label, value, onChange, type = "text", testid }) {
    return (
        <label className="block mb-5">
            <span className="block text-[10px] uppercase tracking-[0.25em] text-bone-500 mb-2">
                {label}
            </span>
            <input
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                data-testid={testid}
                className="w-full bg-transparent border-0 border-b border-white/15 focus:border-ember outline-none py-2 text-bone font-display text-base tracking-tight"
            />
        </label>
    );
}

function ReadOnly({ label, value }) {
    return (
        <div className="mb-6">
            <span className="block text-[10px] uppercase tracking-[0.25em] text-bone-500 mb-2">
                {label}
            </span>
            <div className="text-bone-300 font-display text-base tracking-tight border-b border-white/5 py-2">
                {value || "—"}
            </div>
        </div>
    );
}
